import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronDown,
  FileText,
  Filter,
  GitBranch,
  History,
  MoreHorizontal,
  PanelsTopLeft,
  Pause,
  Pencil,
  Play,
  Plus,
  Search,
  Users,
} from "lucide-react";
import { useStore } from "../lib/store";
import type { Funnel } from "../lib/types";
import { appPath } from "../app/routes";
import { HelpPopover, Menu, type MetricExplanation } from "../ui";
import { CompactSelect } from "../ui/CompactSelect";
import { Spark } from "../ui/Spark";
import {
  HUB_CONFIG_OPTIONS,
  HUB_STATUS_LABELS,
  HUB_VIEWS,
  hubDeltaLabel,
  hubRows,
  hubSummary,
  hubUpdatedLabel,
  matchesHubConfig,
  matchesHubSearch,
  matchesHubView,
  sortHubRows,
  type HubConfig,
  type HubRow,
  type HubSortKey,
  type HubView,
} from "../features/funnels/hub";
import { FunilForm } from "./FunilForm";
import "./lista.css";
import "./funis.css";

/**
 * Funis — a relação do protótipo (`#page-funis`): visões laterais, quatro
 * indicadores, busca e filtros, tabela com situação, participantes, estrutura e
 * ações por funil. Tudo vem do domínio: funis, etapas, participações,
 * rascunhos de flow e versões ativas. Pausar inscrições ainda não existe no
 * runtime, então o interruptor fica desabilitado com o motivo (§18, §27.7).
 */

const PAGE_SIZES = ["25", "50"] as const;
type PageSize = (typeof PAGE_SIZES)[number];
const SCOPE_NOTE = "Respeita a busca, a visão lateral e o filtro de configuração; inclui todas as páginas.";
const COMPARE_NOTE =
  "Compara hoje e os 29 dias anteriores com os 30 dias imediatamente anteriores, usando as datas registradas.";

const HELP: Record<"members" | "funnels" | "active" | "attention", MetricExplanation[]> = {
  members: [
    { term: "Fórmula", description: "Cadastros únicos vinculados aos funis selecionados; um cadastro em dois funis conta uma vez." },
    { term: "Escopo", description: SCOPE_NOTE },
    { term: "Comparação", description: `${COMPARE_NOTE} Usa a data de criação de cada cadastro; não reconstrói entradas antigas.` },
  ],
  funnels: [
    { term: "Fórmula", description: "Processos listados nesta seleção." },
    { term: "Escopo", description: SCOPE_NOTE },
    { term: "Comparação", description: `${COMPARE_NOTE} Usa a última alteração registrada de cada funil; funis sem data não entram.` },
  ],
  active: [
    { term: "Fórmula", description: "Funis com processo publicado e versão ativa." },
    { term: "Escopo", description: SCOPE_NOTE },
    { term: "Comparação", description: `${COMPARE_NOTE} Usa a última alteração registrada dos funis atualmente ativos.` },
  ],
  attention: [
    { term: "Fórmula", description: "Funis com configuração pendente: sem processo ou sem blocos." },
    { term: "Escopo", description: `${SCOPE_NOTE} Mais ocorrências são desfavoráveis.` },
    { term: "Comparação", description: `${COMPARE_NOTE} Usa a última alteração registrada; não representa quando a atenção começou.` },
  ],
};

function ViewIcon({ id }: { id: HubView }) {
  switch (id) {
    case "all":
      return <PanelsTopLeft aria-hidden="true" />;
    case "on":
      return <Play aria-hidden="true" />;
    case "paused":
      return <Pause aria-hidden="true" />;
    case "draft":
      return <FileText aria-hidden="true" />;
  }
}

const SORT_KEYS: HubSortKey[] = ["name", "status", "members", "stages", "updatedAt"];
function isSortKey(value: string | null): value is HubSortKey {
  return SORT_KEYS.includes(value as HubSortKey);
}

export function FunisPage() {
  const store = useStore();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<HubSortKey>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [form, setForm] = useState<{ open: boolean; funnel?: Funnel }>({ open: false });

  useEffect(() => {
    titleRef.current?.focus({ preventScroll: true });
  }, []);

  const viewParam = params.get("visao");
  const view: HubView = HUB_VIEWS.some((item) => item.id === viewParam) ? (viewParam as HubView) : "all";
  const configParam = params.get("config");
  const config: HubConfig = HUB_CONFIG_OPTIONS.some((item) => item.value === configParam) ? (configParam as HubConfig) : "all";
  const sizeParam = params.get("porPagina");
  const pageSize: PageSize = PAGE_SIZES.includes(sizeParam as PageSize) ? (sizeParam as PageSize) : "50";
  const page = Math.max(1, Number(params.get("pagina")) || 1);
  const sortParam = params.get("ordem");
  useEffect(() => {
    if (isSortKey(sortParam)) setSortKey(sortParam);
  }, [sortParam]);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const now = useMemo(() => new Date().toISOString(), []);
  const allRows = useMemo(
    () =>
      hubRows({
        funnels: store.funnels,
        stages: store.stages,
        leads: store.leads,
        memberships: store.memberships,
        flowDrafts: store.flowDrafts,
        flowActiveVersions: store.flowActiveVersions,
      }),
    [store.funnels, store.stages, store.leads, store.memberships, store.flowDrafts, store.flowActiveVersions],
  );
  const searched = useMemo(() => allRows.filter((row) => matchesHubSearch(row, search) && matchesHubConfig(row, config)), [allRows, search, config]);
  const filtered = useMemo(() => sortHubRows(searched.filter((row) => matchesHubView(row, view)), sortKey, sortDirection), [searched, view, sortKey, sortDirection]);
  const summary = useMemo(() => hubSummary(filtered, store.leads, now), [filtered, store.leads, now]);

  const size = Number(pageSize);
  const pages = Math.max(1, Math.ceil(filtered.length / size));
  const currentPage = Math.min(page, pages);
  const start = (currentPage - 1) * size;
  const pageRows = filtered.slice(start, start + size);

  const countOf = (id: HubView) => searched.filter((row) => matchesHubView(row, id)).length;

  const toggleSort = (key: HubSortKey) => {
    if (key === sortKey) setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDirection("asc");
    }
    update({ ordem: key === "name" ? null : key, pagina: null });
  };

  const openEditor = (row: HubRow, aba?: string) =>
    navigate(`${appPath(`funis/${row.funnel.id}`)}${aba ? `?aba=${aba}` : ""}`);

  const cards = [
    { key: "members", label: "Participantes", value: summary.participants, growth: summary.growth.participants, featured: true, help: HELP.members },
    { key: "funnels", label: "Funis", value: summary.funnels, growth: summary.growth.funnels, help: HELP.funnels },
    { key: "active", label: "Ativos", value: summary.active, growth: summary.growth.active, help: HELP.active },
    { key: "attention", label: "Precisam de atenção", value: summary.attention, growth: summary.growth.attention, help: HELP.attention, inverted: true },
  ] as const;

  const format = (value: number) => value.toLocaleString("pt-BR");
  /**
   * O interruptor controla a inscrição do funil: pausar interrompe a entrada de
   * novos cadastros e preserva a versão publicada. Sem versão publicada não há
   * o que pausar, e o motivo fica no `title`.
   */
  const toggleEnrollment = (row: HubRow) => {
    if (!row.draft || row.status === "draft") return;
    store.setFlowEnrollmentPaused(row.draft.id, row.status === "on");
  };

  const sortLabel = (label: string, key: HubSortKey) =>
    key !== sortKey ? `Ordenar por ${label}` : `${label}, em ordem ${sortDirection === "asc" ? "crescente" : "decrescente"}; inverter`;

  return (
    <main className="cx-funis" aria-label="Funis">
      <aside className="z-lead-sidebar">
        <nav className="z-lead-nav" aria-label="Visões de funis">
          {HUB_VIEWS.map((item) => {
            const active = item.id === view;
            return (
              <button
                key={item.id}
                type="button"
                className={active ? "active" : undefined}
                aria-current={active ? "page" : undefined}
                onClick={() => update({ visao: item.id === "all" ? null : item.id, pagina: null })}
              >
                <ViewIcon id={item.id} />
                <span>{item.label}</span>
                <span className="cnt">{countOf(item.id)}</span>
              </button>
            );
          })}
        </nav>
      </aside>
      <section className="z-lead-main" aria-label="Relação de funis">
        <div className="pagehead">
          <div>
            <h1 ref={titleRef} tabIndex={-1}>
              Funis
            </h1>
            <p className="sub">Organize seus processos, conecte etapas e acompanhe cada percurso.</p>
          </div>
          <div className="actions z-inline">
            <button type="button" className="btn-primary" onClick={() => setForm({ open: true })}>
              <Plus size={15} aria-hidden="true" /> Novo funil
            </button>
          </div>
        </div>
        <dl className="z-lead-summary z-growth-grid" aria-label="Resumo dos funis filtrados">
          {cards.map((card) => {
            const diff = card.growth.current - card.growth.before;
            const inverted = "inverted" in card && card.inverted;
            const favorable = inverted ? diff <= 0 : diff >= 0;
            const featured = "featured" in card && card.featured;
            const tone = featured ? "var(--grad-ink)" : diff === 0 ? "var(--muted)" : favorable ? "var(--good)" : "var(--crit)";
            return (
              <div key={card.key} className={["z-growth-card", "kpi", featured ? "kpi-featured" : ""].filter(Boolean).join(" ")}>
                <dt className="label">
                  {card.label}
                  <HelpPopover about={card.label.toLowerCase()} label={`Como é calculado: ${card.label}`} items={card.help} align="end" />
                </dt>
                <dd>
                  <div className="z-growth-body">
                    <div>
                      <strong className={["num", "z-growth-number", card.key === "attention" ? "z-attention-value" : ""].filter(Boolean).join(" ")}>
                        {format(card.value)}
                      </strong>
                    </div>
                  </div>
                  <div className="z-growth-foot">
                    <span className={`delta ${diff === 0 ? "flat" : favorable ? "up" : "down"}`}>{hubDeltaLabel(card.growth, format)}</span>
                    <Spark
                      points={card.growth.bins}
                      color={tone}
                      label={`${card.label} por intervalo: ${card.growth.bins.map(format).join(", ")}`}
                    />
                  </div>
                </dd>
              </div>
            );
          })}
        </dl>
        <div className="z-lead-tools">
          <div className="search">
            <Search size={15} aria-hidden="true" />
            <input
              type="text"
              aria-label="Buscar funis por nome ou descrição"
              placeholder="Buscar funil ou descrição…"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                if (page !== 1) update({ pagina: null });
              }}
            />
          </div>
          <div className="z-hub-filter">
            <CompactSelect
              prefix="Configuração"
              options={HUB_CONFIG_OPTIONS.map((item) => ({ value: item.value, label: item.label }))}
              value={config}
              align="end"
              onChange={(value: HubConfig) => update({ config: value === "all" ? null : value, pagina: null })}
            />
            <CompactSelect
              prefix="Por página"
              options={PAGE_SIZES.map((value) => ({ value, label: value }))}
              value={pageSize}
              align="end"
              onChange={(value: PageSize) => update({ porPagina: value === "50" ? null : value, pagina: null })}
            />
          </div>
        </div>
        <section className="card table-card">
          <div className="table-scroll" role="region" aria-label="Lista de funis" tabIndex={0}>
            <table className="leads" aria-label="Funis da visualização selecionada">
              <colgroup>
                <col style={{ width: "34%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "5%" }} />
              </colgroup>
              <thead>
                <tr>
                  {(
                    [
                      ["name", "Funil"],
                      ["status", "Situação"],
                      ["members", "Participantes"],
                      ["stages", "Estrutura"],
                      ["updatedAt", "Última alteração"],
                    ] as Array<[HubSortKey, string]>
                  ).map(([key, label]) => {
                    const sorted = key === sortKey;
                    return (
                      // O protótipo marca só a coluna ordenada, com um chevron fixo; a direção
                      // fica em `aria-sort`. A classe `sorted` traria o triângulo de `lista.css`.
                      <th key={key} scope="col" aria-sort={sorted ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}>
                        <button type="button" className="z-lead-sort" aria-label={sortLabel(label, key)} onClick={() => toggleSort(key)}>
                          {label}
                          {sorted && <ChevronDown size={12} aria-hidden="true" />}
                        </button>
                      </th>
                    );
                  })}
                  <th scope="col">
                    <span className="z-visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "28px 14px", color: "var(--muted)" }}>
                      {allRows.length === 0
                        ? "Nenhum funil ainda. Crie o primeiro em “Novo funil”."
                        : "Nenhum funil corresponde à busca e aos filtros."}
                    </td>
                  </tr>
                )}
                {pageRows.map((row) => (
                  <tr key={row.funnel.id}>
                    <td>
                      <div className="cf-hub-identity">
                        <span className="cf-hub-icon" style={{ "--cx-tone": "var(--accent)" } as CSSProperties}>
                          <Filter aria-hidden="true" />
                        </span>
                        <div>
                          <button type="button" className="z-hub-name" onClick={() => openEditor(row)}>
                            {row.funnel.name}
                          </button>
                          <span className="z-hub-note" title={row.note}>
                            {row.note}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="cf-hub-state">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={row.status === "on"}
                          aria-label={`${row.status === "on" ? "Pausar" : "Ativar"} ${row.funnel.name}`}
                          title={row.toggleReason}
                          disabled={row.status === "draft"}
                          onClick={() => toggleEnrollment(row)}
                        >
                          <i />
                        </button>
                        <span className={`cx-hub-status ${row.status}`}>
                          <i aria-hidden="true" />
                          {HUB_STATUS_LABELS[row.status]}
                        </span>
                      </div>
                    </td>
                    <td>
                      <button type="button" className="cf-hub-members" title="Ver participantes" onClick={() => openEditor(row, "participantes")}>
                        <strong>{row.participants}</strong>
                        <span>cadastros</span>
                      </button>
                    </td>
                    <td>
                      <span className="cf-hub-structure">
                        <strong>{row.blocks}</strong> blocos <i>·</i>
                        <strong>{row.stages}</strong> etapas
                      </span>
                      {row.pending && <span className="cf-hub-pending">Configuração pendente</span>}
                    </td>
                    <td>
                      <time dateTime={row.updatedAt}>{hubUpdatedLabel(row.updatedAt)}</time>
                    </td>
                    <td>
                      <Menu
                        label={`Ações de ${row.funnel.name}`}
                        className="z-hub-menu"
                        align="end"
                        title={row.funnel.name}
                        sections={[
                          {
                            id: "funnel",
                            items: [
                              { id: "open", label: row.pending ? "Configurar processo" : "Abrir fluxograma", icon: <GitBranch aria-hidden="true" /> },
                              { id: "edit", label: "Editar funil e etapas", icon: <Pencil aria-hidden="true" /> },
                              { id: "members", label: "Ver participantes", icon: <Users aria-hidden="true" /> },
                              { id: "history", label: "Histórico de versões", icon: <History aria-hidden="true" /> },
                            ],
                          },
                        ]}
                        onSelect={(id) => {
                          if (id === "open") openEditor(row);
                          else if (id === "edit") setForm({ open: true, funnel: row.funnel });
                          else if (id === "members") openEditor(row, "participantes");
                          else if (id === "history") openEditor(row, "historico");
                        }}
                        trigger={(args) => (
                          <button type="button" className="cf-icon" title={`Ações de ${row.funnel.name}`} aria-label={`Ações de ${row.funnel.name}`} {...args}>
                            <MoreHorizontal aria-hidden="true" />
                          </button>
                        )}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <footer className="table-foot">
            <span role="status">
              {filtered.length === 0
                ? "0 funis"
                : `${start + 1}–${Math.min(start + size, filtered.length)} de ${filtered.length} ${filtered.length === 1 ? "funil" : "funis"}`}
            </span>
            <div className="z-lead-pagination">
              <div className="pages">
                <button type="button" disabled={currentPage <= 1} onClick={() => update({ pagina: currentPage - 1 <= 1 ? null : String(currentPage - 1) })}>
                  Anterior
                </button>
                <span>
                  {currentPage} / {pages}
                </span>
                <button type="button" disabled={currentPage >= pages} onClick={() => update({ pagina: String(currentPage + 1) })}>
                  Próxima
                </button>
              </div>
            </div>
          </footer>
        </section>
      </section>
      <FunilForm
        open={form.open}
        funnel={form.funnel}
        onClose={() => setForm({ open: false })}
        onSaved={(funnelId) => {
          if (!form.funnel) navigate(appPath(`funis/${funnelId}`));
        }}
      />
    </main>
  );
}
